import React, { useCallback, useEffect, useRef, useState } from "react";

export default function LongTextValue({
  value,
  lines = 3,
  expanded = false,
  onExpandedChange,
}) {
  const previewRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  const text =
    value === null || value === undefined || String(value).trim() === ""
      ? "—"
      : String(value);

  const safeLines = Math.max(1, Number(lines) || 3);

  const checkOverflow = useCallback(() => {
    const element = previewRef.current;

    if (!element) {
      setHasOverflow(false);
      return;
    }

    /*
     * Do not reset overflow while expanded.
     * The expanded element naturally has equal scroll/client height.
     */
    if (expanded) return;

    const verticalOverflow = element.scrollHeight > element.clientHeight + 1;

    const horizontalOverflow = element.scrollWidth > element.clientWidth + 1;

    setHasOverflow(verticalOverflow || horizontalOverflow);
  }, [expanded]);

  useEffect(() => {
    checkOverflow();

    const element = previewRef.current;

    if (!element) return undefined;

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", checkOverflow);

      return () => {
        window.removeEventListener("resize", checkOverflow);
      };
    }

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(element);

    return () => observer.disconnect();
  }, [text, safeLines, checkOverflow]);

  const canToggle = hasOverflow || expanded;

  const toggleExpanded = () => {
    if (!canToggle) return;

    onExpandedChange?.(!expanded);
  };

  const handleKeyDown = (event) => {
    if (!canToggle) return;

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggleExpanded();
    }
  };

  return (
    <div
      className={[
        "safety-long-text",
        hasOverflow ? "safety-long-text--overflow" : "",
        expanded ? "safety-long-text--expanded" : "",
      ]
        .filter(Boolean)
        .join(" ")}
      style={{
        "--safety-long-text-lines": safeLines,
      }}
    >
      <div
        ref={previewRef}
        className="safety-long-text__preview"
        role={canToggle ? "button" : undefined}
        tabIndex={canToggle ? 0 : undefined}
        aria-expanded={canToggle ? expanded : undefined}
        title={
          canToggle
            ? expanded
              ? "Click to collapse text"
              : "Click to show complete text"
            : undefined
        }
        onClick={toggleExpanded}
        onKeyDown={handleKeyDown}
      >
        {text}
      </div>

      {canToggle ? (
        <button
          type="button"
          className="safety-long-text__toggle"
          aria-expanded={expanded}
          onClick={(event) => {
            event.stopPropagation();
            toggleExpanded();
          }}
        >
          <span>{expanded ? "Show less" : "Show full text"}</span>

          <span className="safety-long-text__toggle-icon" aria-hidden="true">
            {expanded ? "↑" : "↓"}
          </span>
        </button>
      ) : null}
    </div>
  );
}
